import '@/amplitude'
import { Provider } from 'react-redux'
import { getProcessorDomainRedirect } from '@/utils/getProcessorDomainRedirect'
import { useLazyGetVenueDetailsQuery } from '@/api/appApi'
import { BlankScreenErrorBoundary } from '@/components/BlankScreenErrorBoundary'
import DownloadApp from '@/components/DownloadApp'
import LoadingApp from '@/components/LoadingApp'
import { Venue } from '@/components/home/types'
import { useApplePay } from '@/hooks/useApplePay'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useRokt } from '@/hooks/useRokt'
import {
  userInfoAtom,
  venueAtom,
  statusAtom,
  paymentMethodsAtom,
  fcmTokenAtom,
  orderingStatusAtom,
  googlePayCredentialsAtom,
  applePayCredentialsAtom,
  applePayMerchantSessionAtom,
  canPayWithApplePayAtom,
  canPayWithGooglePayAtom,
  payWithApplePayAtom,
  payWithGooglePayAtom,
  tabUuidAtom,
  clientVerificationRequiredByVenueAtom,
} from '@/state/global'
import {
  hasPlacedAnOrderOnTabAtom,
  menuModalAtom,
  orderAtom,
  viewOrderAtom,
  orderUuidAtom,
  venueTableAtom,
} from '@/state/order'
import { PaymentDataAtom, switchedPaymentMethodAtom } from '@/state/payment'
import '@/styles/globals.css'
import { findTabsAPI, getApplePaySessionAPI, getGooglePayCredsAPI } from '@/utils/apis'
import {
  API_URL,
  BACK_API_URL,
  CC_ENTRY_MODES,
  GOOGLE_ANALYTICS_ID,
  GOOGLE_PASE_BASE_REQUEST,
  GOOGLE_PAY_ALLOWED_CARD_AUTH_METHODS,
  GOOGLE_PAY_ALLOWED_CARD_NETWORKS,
  GOOGLE_PAY_ENV,
  GOOGLE_PAY_PAYMENT_METHOD_TYPE,
} from '@/utils/constants'
import {
  filterCompatiblePaymentMethods,
  hasCompatibleManualKeyedCard,
  isHostedPageVenue,
  isPaymentMethodCompatibleWithVenue,
} from '@/utils/payment.utils'
import { formatGlobalAttributes, newRelicLog } from '@/utils/newrelic'
import { ApplePayCredentials, GooglePayCredentials, User } from '@/utils/types'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAtom, useSetAtom } from 'jotai'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { PropsWithChildren, useEffect, useState } from 'react'
import { store } from '@/state/store'
import amplitude from '@/amplitude'

declare global {
  interface Window {
    ApplePaySession: any
    newrelic: any
  }
}

const queryClient = new QueryClient()

export default function defaultAppWithStore({ Component, pageProps }: AppProps) {
  return (
    <BlankScreenErrorBoundary>
      <Provider store={store}>
        <App>
          <Component {...pageProps} />
        </App>
      </Provider>
    </BlankScreenErrorBoundary>
  )
}

const STATUS_PAGE_PATHS = ['/status', '/status-details']

function App({ children }: PropsWithChildren) {
  const router = useRouter()
  const isMobile = useIsMobile()
  const appContentMaxWidth = !isMobile && STATUS_PAGE_PATHS.includes(router.pathname) ? 'max-w-[80%]' : 'max-w-[820px]'
  const { venue_uuid, tab_uuid, ordering, s2p, table_name, v } = router.query

  useEffect(() => {
    // Push the current state to the history stack
    window.history.pushState(null, '', window.location.href)

    const handleBackButtonEvent = (e: { preventDefault: () => void }) => {
      e.preventDefault()
      // You can add logic here to preserve query parameters or handle the back button specifically
      // For now, this will simply navigate to the current path
      router.push(router.asPath)
    }

    // Listen for popstate events (triggered by the browser's back button)
    window.addEventListener('popstate', handleBackButtonEvent)

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handleBackButtonEvent)
    }
  }, [router])

  const [savedTabUuid, setSavedTabUuid] = useAtom(tabUuidAtom)
  const setOrderUuid = useSetAtom(orderUuidAtom)
  const [user, setUser] = useAtom(userInfoAtom)
  const setNotificationToken = useSetAtom(fcmTokenAtom)
  const [status, setStatus] = useAtom(statusAtom)
  const [paymentMethods, setPaymentMethods] = useAtom(paymentMethodsAtom)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useAtom(PaymentDataAtom)
  const setOrder = useSetAtom(orderAtom)
  const setViewOrder = useSetAtom(viewOrderAtom)
  const setOpenMenu = useSetAtom(menuModalAtom)
  const setOrderingStatus = useSetAtom(orderingStatusAtom)
  const setGooglePayCredentials = useSetAtom(googlePayCredentialsAtom)
  const setApplePayCredentials = useSetAtom(applePayCredentialsAtom)
  const setCanPayWithApplePay = useSetAtom(canPayWithApplePayAtom)
  const [canPayWithGooglePay, setCanPayWithGooglePay] = useAtom(canPayWithGooglePayAtom)
  const [payWithApplePay, setPayWithApplePay] = useAtom(payWithApplePayAtom)
  const [payWithGooglePay, setPayWithGooglePay] = useAtom(payWithGooglePayAtom)
  const [venue, setVenue] = useAtom(venueAtom)
  const setClientVerificationByVenue = useSetAtom(clientVerificationRequiredByVenueAtom)
  const setHasPlacedAnOrderOnTab = useSetAtom(hasPlacedAnOrderOnTabAtom)
  const setDidSwitchPaymentMethod = useSetAtom(switchedPaymentMethodAtom)
  const setSelectedTable = useSetAtom(venueTableAtom)

  // Initialize ROKT hook
  const { script: roktScript } = useRokt()

  // if a payment is already selected and saved in local storage we want to make sure its still available
  const paymentIdFromLocalStorage = typeof window !== 'undefined' ? localStorage.getItem('paymentMethods') : false
  const localStoragePaymentExists = paymentIdFromLocalStorage && paymentIdFromLocalStorage != 'null'

  const [getVenueDetails, result] = useLazyGetVenueDetailsQuery()
  useEffect(() => {
    if (venue_uuid) {
      getVenueDetails(venue_uuid as string)
    } else if (v) {
      getVenueDetails(v as string)
    }
  }, [venue_uuid, v])

  useEffect(() => {
    const { isSuccess, data } = result
    if (isSuccess) {
      setVenue(data)
      if (!data) {
        setShowDownloadAppScreen(true)
        setLoading(false)
      } else {
        const redirectUrl = getProcessorDomainRedirect(data.payment_processor)
        if (redirectUrl) {
          window.location.href = redirectUrl
          return
        }
        if (!data.mobile_menu_enabled || !data.ordering_enabled) {
          setOrderingStatus(false)
        }
        if (!canPayWithGooglePay && data?.cc_entry_mode === 'no_keyed_entry') {
          setShowDownloadAppScreen(true)
          /**
           * continue showing the loading screen until we know whether the venue allows
           * google pay
           */
          setLoading(true)
        } else {
          if (router.route.includes('404')) {
            const venueAndOrdering = `?venue_uuid=${venue_uuid}${ordering === 'false' ? '&ordering=false' : ''}`
            if (tab_uuid) {
              router.push(`/tab/${venueAndOrdering}&tab_uuid=${tab_uuid}${s2p === 'true' ? 's2p=true' : ''}`)
            } else {
              router.push(`/${venueAndOrdering}`)
            }
          } else {
            setLoading(false)
          }
        }
      }
    }
  }, [result])

  const [loading, setLoading] = useState(true)

  const openCart = () => {
    setTimeout(() => {
      setViewOrder(true)
    }, 250)

    setOpenMenu(true)
  }

  const [showDownloadAppScreen, setShowDownloadAppScreen] = useState(false)

  useEffect(() => {
    const fetchPayCredentials = async () => {
      let googlePayAvailable = false
      let applePayAvailable = false
      const isFortis = venue?.payment_processor === 'fortis'
      const isMagensaUnigate = venue?.payment_processor === 'magensa_unigate'

      // Reset wallet availability so a venue switch never carries stale state forward.
      setCanPayWithApplePay(false)
      setCanPayWithGooglePay(false)

      if (venue?.venue_uuid && (isFortis || isMagensaUnigate)) {
        // Apple Pay credentials — Fortis and Magensa Unigate
        if ((isFortis || isMagensaUnigate) && venue?.apple_pay_enabled) {
          const applePayCredentials: ApplePayCredentials | null = await getApplePaySessionAPI(
            venue.venue_uuid,
            venue.payment_processor as string
          )
          setApplePayCredentials(applePayCredentials)

          if (typeof window !== 'undefined' && applePayCredentials?.merchantId) {
            const hasApplePaySession = !!window?.ApplePaySession
            if (hasApplePaySession) {
              applePayAvailable = window.ApplePaySession.canMakePayments()
            }
          }
          setCanPayWithApplePay(applePayAvailable)
        }

        // Google Pay credentials — both Fortis and Magensa Unigate
        if (venue?.google_pay_enabled) {
          const googlePaycredentials: GooglePayCredentials | null = await getGooglePayCredsAPI(
            venue.venue_uuid,
            venue.payment_processor as string
          )
          setGooglePayCredentials(googlePaycredentials)

          if (typeof window !== 'undefined') {
            const newGooglePayClient = new google.payments.api.PaymentsClient({ environment: GOOGLE_PAY_ENV })
            const isReadyToPayResponse: google.payments.api.IsReadyToPayResponse = await newGooglePayClient.isReadyToPay({
              ...GOOGLE_PASE_BASE_REQUEST,
              allowedPaymentMethods: [
                {
                  type: GOOGLE_PAY_PAYMENT_METHOD_TYPE,
                  parameters: {
                    allowedAuthMethods: GOOGLE_PAY_ALLOWED_CARD_AUTH_METHODS,
                    allowedCardNetworks: GOOGLE_PAY_ALLOWED_CARD_NETWORKS,
                  },
                },
              ],
            })
            googlePayAvailable = isReadyToPayResponse.result
            setCanPayWithGooglePay(googlePayAvailable)
            if (loading) {
              setLoading(false)
            }
            if (googlePayAvailable) {
              /**
               * venue offers google pay, so don't show the download app screen
               */
              setShowDownloadAppScreen(false)
            }
          }
        } else {
          if (loading) {
            /**
             * this means user is on android and must be shown the download
             * app screen via setting loading = false b/c we know
             */
            setLoading(false)
          }
        }

        if (payWithApplePay && !applePayAvailable) {
          setPayWithApplePay(false)
          if (googlePayAvailable) {
            setPayWithGooglePay(true)
          }
        } else if (payWithGooglePay && !googlePayAvailable) {
          setPayWithGooglePay(false)
          if (applePayAvailable) {
            setPayWithApplePay(true)
          }
        } else if ((selectedPaymentMethod || localStoragePaymentExists) && !CC_ENTRY_MODES.includes(venue?.cc_entry_mode)) {
          setSelectedPaymentMethod(null)
          localStorage.removeItem('paymentMethods')
          setPaymentMethods([])
          if (applePayAvailable) {
            setPayWithApplePay(true)
          } else if (googlePayAvailable) {
            setPayWithGooglePay(true)
          }
        }

        // When switching between hosted-page venues (e.g. Magensa → Fortis), clear any
        // manual-keyed cards that belong to the previous processor — they cannot be charged
        // at the current venue even though CC_ENTRY_MODES includes both processors.
        if (isHostedPageVenue(venue?.cc_entry_mode)) {
          const filteredMethods = filterCompatiblePaymentMethods(paymentMethods, venue.cc_entry_mode)
          if (filteredMethods.length !== paymentMethods.length) {
            setPaymentMethods(filteredMethods)
            localStorage.setItem('paymentMethods', JSON.stringify(filteredMethods))
          }

          const selectedIsIncompatible =
            !!selectedPaymentMethod && !isPaymentMethodCompatibleWithVenue(selectedPaymentMethod, venue.cc_entry_mode)
          if (selectedIsIncompatible) {
            setSelectedPaymentMethod(null)
          }

          // If no compatible manual-keyed card will be available (stored or selected)
          // and no wallet is already active, fall back to Apple Pay or Google Pay.
          const effectiveSelected = selectedIsIncompatible ? null : selectedPaymentMethod
          if (!payWithApplePay && !payWithGooglePay && !effectiveSelected && !hasCompatibleManualKeyedCard(filteredMethods)) {
            if (applePayAvailable) {
              setPayWithApplePay(true)
            } else if (googlePayAvailable) {
              setPayWithGooglePay(true)
            }
          }
        }

        // if no payment is selected we select in order of precedence: Apple, Google
        if (!payWithApplePay && !payWithGooglePay && !selectedPaymentMethod && !localStoragePaymentExists) {
          if (applePayAvailable) {
            setPayWithApplePay(true)
            setPayWithGooglePay(false)
            setSelectedPaymentMethod(null)
          } else if (googlePayAvailable) {
            setPayWithApplePay(false)
            setPayWithGooglePay(true)
            setSelectedPaymentMethod(null)
          } else {
            setPayWithApplePay(false)
            setPayWithGooglePay(false)
            if (venue?.cc_entry_mode === 'no_keyed_entry') {
              setShowDownloadAppScreen(true)
            }
          }
        }
      }
    }

    fetchPayCredentials()
  }, [venue?.venue_uuid])

  useEffect(() => {
    if (!venue_uuid && router.isReady) {
      setTimeout(() => {
        setLoading(false)
      }, 500)
      return
    } else if (venue && Object.keys(venue).length) {
      const searchForUserTab = async (
        storedPaymentId: any,
        storedUser: User | null,
        venueDetails: Venue,
        orderingIsEnabled: boolean
      ) => {
        if (Array.isArray(storedPaymentId) && storedUser) {
          const cards_last_four = storedPaymentId.map((paymentData) => paymentData?.pan_mask.slice(-4))
          let findTabsAPIResult: { tabs?: any[] } | null = null
          try {
            findTabsAPIResult = await findTabsAPI(
              cards_last_four,
              venueDetails.latitude,
              venueDetails.longitude,
              storedUser as User
            )
          } catch (err) {
            console.error('findTabsAPI failed during startup:', err)
            const serializedError =
              err instanceof Error
                ? err.message
                : (() => {
                    try {
                      return JSON.stringify(err)
                    } catch {
                      return String(err)
                    }
                  })()
            newRelicLog(
              {
                name: 'findTabsAPIStartupError',
                isSuccessful: 'false',
                errorMessage: serializedError,
              },
              'pageAction'
            )
            findTabsAPIResult = { tabs: [] }
          }
          if (findTabsAPIResult?.tabs && findTabsAPIResult.tabs.length > 0) {
            const userTabsAtThisVenue = findTabsAPIResult.tabs.filter(
              (foundTab: any) => (venue_uuid as string).toUpperCase() === foundTab?.venue_uuid.toUpperCase()
            )

            if (userTabsAtThisVenue.length > 0) {
              const mostRecentTabUuid = userTabsAtThisVenue[userTabsAtThisVenue.length - 1].tab_uuid
              setSavedTabUuid(mostRecentTabUuid)
              await router.push(
                `/tab/?venue_uuid=${venue_uuid}&tab_uuid=${mostRecentTabUuid}${ordering === 'false' ? '&ordering=false' : ''}${
                  s2p === 'true' ? '&s2p=true' : ''
                }`
              )
              return mostRecentTabUuid
            }
          }

          return null
        }
      }

      const setUpWebClip = async () => {
        if (!router.isReady) return

        if (!venue_uuid && router.isReady) {
          setTimeout(() => {
            setLoading(false)
          }, 500)
          return
        }

        if (window) {
          const venue_uuidSaved = localStorage.getItem('venue_uuid')
          const userFromLocalStorage = localStorage.getItem('user')
          const notificationTokenFromLocalStorage = localStorage.getItem('fcmToken')
          const statusFromLocalStorage = localStorage.getItem('status')

          let mostRecentTabUuid = savedTabUuid
          let orderingIsEnabled = Boolean(venue?.ordering_enabled)
          if (ordering) {
            orderingIsEnabled = ordering !== 'false'
          }

          // reinitialize user
          let storedUser: User | null

          // we want to check for tab even if there is no user for scan2pay
          const urlHasTab = window.location.href.includes('/tab') || window.location.href.includes('tab_uuid')

          if ((!userFromLocalStorage || userFromLocalStorage === 'null') && !user && !urlHasTab) {
            await router.push(`/?venue_uuid=${venue_uuid}${ordering === 'false' ? '&ordering=false' : ''}`)
          }

          storedUser = userFromLocalStorage ? (JSON.parse(userFromLocalStorage) as User) : user
          setUser(storedUser)
          amplitude.setUserId(storedUser?.email ?? undefined)

          const storedStatus = statusFromLocalStorage ? JSON.parse(statusFromLocalStorage) : status
          setStatus(storedStatus)

          // reinitialize payments
          let storedPaymentId: any
          if (localStoragePaymentExists) {
            storedPaymentId = JSON.parse(paymentIdFromLocalStorage ?? '')
            setPaymentMethods(storedPaymentId)
            setSelectedPaymentMethod(storedPaymentId[0])
          }

          // if we're at a different venue than what's in local storage we need to do some cleanup

          if (venue_uuid && (venue_uuid as string).toUpperCase() !== venue_uuidSaved?.toUpperCase()) {
            localStorage.setItem('venue_uuid', venue_uuid as string)
            setOrderUuid('')

            if (tab_uuid) {
              setSavedTabUuid(tab_uuid as string)
            } else {
              setSavedTabUuid('')
              mostRecentTabUuid = await searchForUserTab(storedPaymentId, storedUser, venue, orderingIsEnabled)
            }

            // if we didn't have the tab and we didn't find the tab we clear it

            setHasPlacedAnOrderOnTab(false)
            setDidSwitchPaymentMethod(false)

            localStorage.removeItem('table_name')
            setSelectedTable(null)
            localStorage.removeItem('order')
          } else {
            // if we are revisting the same venue lets see if they have a tab and redirect to that

            // first lets check for a saved tab_uuid. This will save us an api call

            if (venue_uuid && savedTabUuid && !tab_uuid) {
              await router.push(
                `/tab/?venue_uuid=${venue_uuid}&tab_uuid=${savedTabUuid}${ordering === 'false' ? '&ordering=false' : ''}`
              )
            } else {
              // if no saved tab uuid, lets hit the backend for the users tabs
              mostRecentTabUuid = await searchForUserTab(storedPaymentId, storedUser, venue, orderingIsEnabled)
            }
          }

          if (savedTabUuid || tab_uuid || mostRecentTabUuid) {
            localStorage.removeItem('order')
          }
          const orderFromLocal = localStorage.getItem('order')
          if (orderFromLocal) {
            setOrder(JSON.parse(orderFromLocal))
            // dont want to reset orderUUID here in case of resubmit of the same order due to network issues
            setTimeout(() => {
              openCart()
            }, 700)
          }

          if (notificationTokenFromLocalStorage) {
            const storedNotificationToken = JSON.parse(notificationTokenFromLocalStorage)
            setNotificationToken(storedNotificationToken)
          }

          const storedTableName = localStorage.getItem('table_name')
          if (storedTableName || table_name) {
            const tableNameToUse = table_name ? table_name : storedTableName

            const foundTable = venue.venue_tables.find((table) => table.name === tableNameToUse)

            if (foundTable) {
              localStorage.setItem('table_name', foundTable.name)
              setSelectedTable(foundTable)
            }

            if (table_name && orderingIsEnabled && !savedTabUuid) {
              setOpenMenu(true)
            }
          }

          if (ordering === 'false') {
            setOrderingStatus(false)
          }

          // check if the user session and status is expired. If it is lets remove all user info and payment methods
          // we do this at the end because if the user has a tab after we look for one, we dont want to invalidate them
          const foundUserTab = window.location.href.includes('/tab') || window.location.href.includes('tab_uuid')
          const verifiedTime = localStorage.getItem('verifiedTime')
          if (verifiedTime) {
            const verifiedTimeInSeconds = parseInt(verifiedTime)
            const nowTimeInSeconds = Date.now()
            if (verifiedTimeInSeconds < nowTimeInSeconds && !foundUserTab) {
              //cleanup everything user, session, and payment related
              localStorage.removeItem('user')
              setUser(null)
              localStorage.removeItem('status')
              setStatus('not_verified')
              localStorage.removeItem('paymentMethods')
              localStorage.removeItem('client-verification-required')
              setClientVerificationByVenue({})
              setPaymentMethods([])
              setSelectedPaymentMethod(null)
              localStorage.removeItem('verifiedTime')
              await router.push(`/?venue_uuid=${venue_uuid}${ordering === 'false' ? '&ordering=false' : ''}`)
            }
          }
        }
      }

      setUpWebClip()

      // disable zoom
      const preventZoom = (event: TouchEvent) => {
        if (event.touches.length > 1) {
          event.preventDefault()
        }
      }

      // Prevent double-tap zoom
      const preventDoubleTap = (event: TouchEvent) => {
        const touch = event.touches[0]
        const currentTime = new Date().getTime()
        const tapLength = currentTime - lastTouchEnd

        if (tapLength < 500 && tapLength > 0) {
          event.preventDefault()
        }

        lastTouchEnd = currentTime
      }

      let lastTouchEnd = 0
      document.addEventListener('touchmove', preventZoom, { passive: false })
      document.addEventListener('touchend', preventDoubleTap, { passive: false })

      // Cleanup function
      return () => {
        document.removeEventListener('touchmove', preventZoom)
        document.removeEventListener('touchend', preventDoubleTap)
      }
    }
  }, [venue_uuid, ordering, venue])

  useEffect(() => {
    const globalAttributes = formatGlobalAttributes(user, venue, tab_uuid as string)
    newRelicLog(globalAttributes, 'setCustomAttributes')
  }, [user, venue, tab_uuid])

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
        <title>{`${venue?.name ?? 'Mobile Ordering'} | Union`}</title>
      </Head>
      {/* Google tag (gtag.js) */}
      <Script async src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`} />
      <Script
        async
        id="google-analytics"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GOOGLE_ANALYTICS_ID}');
            `,
        }}
      />
      {venue?.rokt_api_key && venue?.rokt_domain && (
        <Script
          id="rokt-sdk"
          type="text/javascript"
          dangerouslySetInnerHTML={{
            __html: roktScript,
          }}
        />
      )}

      <QueryClientProvider client={queryClient}>
        {loading ? (
          <LoadingApp />
        ) : (
          <div className="mx-auto max-w-[820px]">
            {(venue?.payment_processor === 'fortis' || venue?.payment_processor === 'magensa_unigate') &&
            !showDownloadAppScreen ? (
              <div id="app-content" className="select-none touch-none">
                {children}
              </div>
            ) : (
              <DownloadApp thankYouPage={false} />
            )}
          </div>
        )}
      </QueryClientProvider>
    </>
  )
}
